import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import {
	createSessionRefresher,
	isSessionRejected
} from "../../services/sessionRefresh";
import toastError from "../../errors/toastError";
import { clearTicketsCache } from "../useTickets";
import { clearContactsCache } from "../../cache/contactsCache";

const getStoredToken = () => {
	const storedToken = localStorage.getItem("token");

	if (!storedToken) {
		return "";
	}

	try {
		return JSON.parse(storedToken) || "";
	} catch (error) {
		return "";
	}
};

const syncAuthorization = token => {
	if (token) {
		localStorage.setItem("token", JSON.stringify(token));
		api.defaults.headers.Authorization = `Bearer ${token}`;
		return;
	}

	localStorage.removeItem("token");
	delete api.defaults.headers.Authorization;
	clearTicketsCache();
	clearContactsCache();
};

const MIN_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

let lastRefreshAt = 0;

const refreshSession = createSessionRefresher(() =>
	api.post("/auth/refresh_token")
);

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	useEffect(() => {
		const requestInterceptor = api.interceptors.request.use(
			config => {
				const token = getStoredToken();
				if (token) {
					config.headers.Authorization = `Bearer ${token}`;
				}
				return config;
			},
			error => Promise.reject(error)
		);

		const responseInterceptor = api.interceptors.response.use(
			response => response,
			async error => {
				const originalRequest = error.config || {};
				const status = error?.response?.status;
				const requestUrl = originalRequest.url || "";
				const isRefreshRequest = requestUrl.includes("/auth/refresh_token");
				const isLoginRequest = requestUrl.includes("/auth/login");

				if (
					(status === 401 || status === 403) &&
					!originalRequest._retry &&
					!isRefreshRequest &&
					!isLoginRequest
				) {
					originalRequest._retry = true;

					try {
						const data = await refreshSession();
						if (data?.token) {
							lastRefreshAt = Date.now();
							syncAuthorization(data.token);
							setIsAuth(true);
							if (data.user) {
								setUser(data.user);
							}
							window.dispatchEvent(new Event("token:refreshed"));
							originalRequest.headers = {
								...originalRequest.headers,
								Authorization: `Bearer ${data.token}`
							};
							return api(originalRequest);
						}
					} catch (refreshError) {
						// Solo un 401 del servidor significa sesion vencida; un fallo de
						// red o 5xx no debe sacar al usuario.
						if (isSessionRejected(refreshError)) {
							syncAuthorization("");
							setIsAuth(false);
							setUser({});
							window.dispatchEvent(new Event("token:refreshed"));
						}
						return Promise.reject(refreshError);
					}
				}

				return Promise.reject(error);
			}
		);

		return () => {
			api.interceptors.request.eject(requestInterceptor);
			api.interceptors.response.eject(responseInterceptor);
		};
	}, []);

	useEffect(() => {
		const token = getStoredToken();

		if (token) {
			api.defaults.headers.Authorization = `Bearer ${token}`;
		}

		(async () => {
			if (token) {
				try {
					const data = await refreshSession();
					lastRefreshAt = Date.now();
					syncAuthorization(data.token);
					setIsAuth(true);
					setUser(data.user);
					window.dispatchEvent(new Event("token:refreshed"));
				} catch (err) {
					if (isSessionRejected(err)) {
						syncAuthorization("");
					}
					toastError(err);
				}
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		if (!isAuth) {
			return undefined;
		}

		// El token dura 15 minutos: al volver a la pestana tras inactividad se
		// renueva una sola vez antes de que la pantalla lance sus peticiones.
		const handleVisible = async () => {
			if (
				document.visibilityState !== "visible" ||
				Date.now() - lastRefreshAt < MIN_REFRESH_INTERVAL_MS
			) {
				return;
			}

			try {
				const data = await refreshSession();
				lastRefreshAt = Date.now();
				syncAuthorization(data.token);
				window.dispatchEvent(new Event("token:refreshed"));
			} catch (err) {
				if (isSessionRejected(err)) {
					syncAuthorization("");
					setIsAuth(false);
					setUser({});
					window.dispatchEvent(new Event("token:refreshed"));
				}
			}
		};
		document.addEventListener("visibilitychange", handleVisible);

		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user);
			}
		});

		return () => {
			document.removeEventListener("visibilitychange", handleVisible);
			socket.disconnect();
		};
	}, [isAuth, user]);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			clearTicketsCache();
			clearContactsCache();
			syncAuthorization(data.token);
			setUser(data.user);
			setIsAuth(true);
			window.dispatchEvent(new Event("token:refreshed"));
			toast.success(i18n.t("auth.toasts.success"));
			history.push("/");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			syncAuthorization("");
			window.dispatchEvent(new Event("token:refreshed"));
			setLoading(false);
			history.push("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
